const { Component, Mixin } = Shopware;
const { Criteria } = Shopware.Data;

Component.register('topdata-es-synonym-suggestion-list', {
    template: `
        <sw-page>
            <template #content>
                <sw-alert
                    v-if="!llmProviderAvailable"
                    variant="warning"
                    :showIcon="true"
                    :closable="false"
                >
                    {{ $tc('TopdataEnhancedSearchSW6.topdata-es-synonym-suggestion.list.llmProviderMissing') }}
                </sw-alert>

                <sw-entity-listing
                    v-if="suggestions"
                    :data-source="suggestions"
                    :columns="columns"
                    :repository="repository"
                    :showSelection="true"
                    :showSettings="false"
                    :showPagination="true"
                    :allowColumnEdit="false"
                    :allowView="false"
                    :allowDelete="false"
                    :fullPage="true"
                    :isLoading="isLoading"
                    :sortBy="sortBy"
                    :sortDirection="sortDirection"
                    @column-sort="onSortColumn"
                    @page-change="onPageChange"
                >
                    <template #column-languageId="{ item }">
                        {{ getLanguageLabel(item.languageId) }}
                    </template>

                    <template #column-status="{ item }">
                        <sw-label
                            :variant="item.status === 'approved' ? 'success' : item.status === 'rejected' ? 'danger' : 'info'"
                            appearance="pill"
                        >
                            {{ $tc('TopdataEnhancedSearchSW6.topdata-es-synonym-suggestion.list.status.' + item.status) }}
                        </sw-label>
                    </template>

                    <template #column-suggestionType="{ item }">
                        {{ $tc('TopdataEnhancedSearchSW6.topdata-es-synonym-suggestion.list.type.' + item.suggestionType) }}
                    </template>

                    <template #column-createdAt="{ item }">
                        <sw-time-ago v-if="item.createdAt" :date="item.createdAt" />
                    </template>

                    <template #actions="{ item }">
                        <sw-context-menu-item
                            v-if="item.status === 'pending'"
                            variant="success"
                            @click="onApprove(item)"
                        >
                            {{ $tc('TopdataEnhancedSearchSW6.topdata-es-synonym-suggestion.list.approveButton') }}
                        </sw-context-menu-item>

                        <sw-context-menu-item
                            v-if="item.status === 'pending'"
                            variant="danger"
                            @click="onReject(item)"
                        >
                            {{ $tc('TopdataEnhancedSearchSW6.topdata-es-synonym-suggestion.list.rejectButton') }}
                        </sw-context-menu-item>
                    </template>

                    <template #bulk-additional>
                        <sw-button
                            variant="success"
                            :disabled="selectionCount === 0"
                            @click="onBatchApprove"
                        >
                            {{ $tc('TopdataEnhancedSearchSW6.topdata-es-synonym-suggestion.list.batchApproveButton') }}
                        </sw-button>

                        <sw-button
                            variant="danger"
                            :disabled="selectionCount === 0"
                            @click="onBatchReject"
                        >
                            {{ $tc('TopdataEnhancedSearchSW6.topdata-es-synonym-suggestion.list.batchRejectButton') }}
                        </sw-button>
                    </template>
                </sw-entity-listing>

                <topdata-es-synonym-suggestion-form
                    v-if="showSuggestionForm"
                    :suggestion="selectedSuggestion"
                    @confirm="onApproveConfirm"
                    @close="showSuggestionForm = false"
                />
            </template>

            <template #smart-bar-header>
                <h2>{{ $tc('TopdataEnhancedSearchSW6.topdata-es-synonym-suggestion.list.title') }}</h2>
            </template>

            <template #smart-bar-actions>
                <sw-single-select
                    v-model:value="filterLanguage"
                    :options="languageOptions"
                    :placeholder="$tc('TopdataEnhancedSearchSW6.topdata-es-synonym-suggestion.list.filterLanguagePlaceholder')"
                    :show-clearable="true"
                    class="topdata-es-synonym-suggestion-language-filter"
                    @update:value="onFilterChange"
                />

                <sw-button
                    variant="primary"
                    :isLoading="isGenerating"
                    @click="onGenerate"
                >
                    {{ $tc('TopdataEnhancedSearchSW6.topdata-es-synonym-suggestion.list.generateButton') }}
                </sw-button>
            </template>
        </sw-page>
    `,

    inject: ['repositoryFactory'],

    mixins: [
        Mixin.getByName('listing'),
    ],

    data() {
        return {
            suggestions: null,
            isLoading: false,
            isGenerating: false,
            showSuggestionForm: false,
            selectedSuggestion: null,
            sortBy: 'createdAt',
            sortDirection: 'DESC',
            llmProviderAvailable: true,
            filterLanguage: null,
        };
    },

    created() {
        Shopware.Application.getContainer('init').httpClient.get('/_action/topdata-enhanced-search-sw6/feature-availability')
            .then((response) => {
                this.llmProviderAvailable = !!response.data.llmProviderAvailable;
            })
            .catch(() => {
                this.llmProviderAvailable = false;
            });
    },

    computed: {
        repository() {
            return this.repositoryFactory.create('tdes_synonym_suggestion');
        },

        languageOptions() {
            return [{
                value: null,
                label: this.$tc('TopdataEnhancedSearchSW6.topdata-es-synonym-suggestion.list.filterLanguageAll'),
            }, {
                value: 'de',
                label: 'Deutsch',
            }, {
                value: 'en',
                label: 'English',
            }, {
                value: 'fr',
                label: 'Français',
            }, {
                value: 'NULL',
                label: this.$tc('TopdataEnhancedSearchSW6.topdata-es-synonym-suggestion.list.filterLanguageGlobal'),
            }];
        },

        columns() {
            return [
                {
                    property: 'term',
                    label: this.$tc('TopdataEnhancedSearchSW6.topdata-es-synonym-suggestion.list.columnTerm'),
                    allowResize: true,
                },
                {
                    property: 'languageId',
                    label: this.$tc('TopdataEnhancedSearchSW6.topdata-es-synonym-suggestion.list.columnLanguage'),
                    allowResize: true,
                },
                {
                    property: 'suggestedSynonyms',
                    label: this.$tc('TopdataEnhancedSearchSW6.topdata-es-synonym-suggestion.list.columnSuggestedSynonyms'),
                    allowResize: true,
                },
                {
                    property: 'targetTerm',
                    label: this.$tc('TopdataEnhancedSearchSW6.topdata-es-synonym-suggestion.list.columnTargetTerm'),
                    allowResize: true,
                },
                {
                    property: 'status',
                    label: this.$tc('TopdataEnhancedSearchSW6.topdata-es-synonym-suggestion.list.columnStatus'),
                    allowResize: true,
                },
                {
                    property: 'suggestionType',
                    label: this.$tc('TopdataEnhancedSearchSW6.topdata-es-synonym-suggestion.list.columnSuggestionType'),
                    allowResize: true,
                },
                {
                    property: 'searchCount',
                    label: this.$tc('TopdataEnhancedSearchSW6.topdata-es-synonym-suggestion.list.columnSearchCount'),
                    allowResize: true,
                },
                {
                    property: 'zeroCount',
                    label: this.$tc('TopdataEnhancedSearchSW6.topdata-es-synonym-suggestion.list.columnZeroCount'),
                    allowResize: true,
                },
                {
                    property: 'createdAt',
                    label: this.$tc('TopdataEnhancedSearchSW6.topdata-es-synonym-suggestion.list.columnCreatedAt'),
                    allowResize: true,
                },
            ];
        },

        selectionCount() {
            return this.selection ? Object.keys(this.selection).length : 0;
        },
    },

    methods: {
        getList() {
            this.isLoading = true;

            const criteria = new Criteria(this.page, this.limit);
            criteria.addSorting(Criteria.sort(this.sortBy, this.sortDirection, this.naturalSorting));

            if (this.filter && this.filter.status) {
                criteria.addFilter(Criteria.equals('status', this.filter.status));
            }

            if (this.filterLanguage) {
                if (this.filterLanguage === 'NULL') {
                    criteria.addFilter(Criteria.equals('languageId', null));
                } else {
                    criteria.addFilter(Criteria.equals('languageId', this.filterLanguage));
                }
            }

            return this.repository.search(criteria).then((result) => {
                this.suggestions = result;
                this.total = result.total;
                this.isLoading = false;
            }).catch(() => {
                this.isLoading = false;
            });
        },

        onFilterChange() {
            this.page = 1;
            this.getList();
        },

        getLanguageLabel(languageId: string | null): string {
            if (!languageId) {
                return this.$tc('TopdataEnhancedSearchSW6.topdata-es-synonym-suggestion.list.languageGlobal');
            }
            const labels: Record<string, string> = {
                'de': 'Deutsch',
                'en': 'English',
                'fr': 'Français',
            };
            return labels[languageId] || languageId;
        },

        onApprove(item) {
            this.selectedSuggestion = item;
            this.showSuggestionForm = true;
        },

        onApproveConfirm(synonyms, languageId) {
            const id = this.selectedSuggestion.id;
            const params = {
                suggested_synonyms: synonyms,
                language_id: languageId || null,
            };

            Shopware.Application.getContainer('init').httpClient.post(
                `/_action/topdata-enhanced-search-sw6/suggestions/${id}/approve`,
                params,
                { headers: { 'Content-Type': 'application/json' } }
            ).then(() => {
                this.showSuggestionForm = false;
                this.selectedSuggestion = null;
                this.getList();
            });
        },

        onReject(item) {
            Shopware.Application.getContainer('init').httpClient.post(
                `/_action/topdata-enhanced-search-sw6/suggestions/${item.id}/reject`
            ).then(() => {
                this.getList();
            });
        },

        onBatchApprove() {
            const ids = Object.keys(this.selection);
            if (ids.length === 0) return;

            Shopware.Application.getContainer('init').httpClient.post(
                '/_action/topdata-enhanced-search-sw6/suggestions/batch-approve',
                { ids },
                { headers: { 'Content-Type': 'application/json' } }
            ).then(() => {
                this.selection = {};
                this.getList();
            });
        },

        onBatchReject() {
            const ids = Object.keys(this.selection);
            if (ids.length === 0) return;

            Promise.all(ids.map((id) =>
                Shopware.Application.getContainer('init').httpClient.post(`/_action/topdata-enhanced-search-sw6/suggestions/${id}/reject`)
            )).then(() => {
                this.selection = {};
                this.getList();
            });
        },

        onGenerate() {
            this.isGenerating = true;

            Shopware.Application.getContainer('init').httpClient.post(
                '/_action/topdata-enhanced-search-sw6/suggestions/generate'
            ).then(() => {
                this.isGenerating = false;
                this.getList();
            }).catch(() => {
                this.isGenerating = false;
            });
        },
    },
});